import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { History, Plus, Trash2, X, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { money, shortDate, PROPERTY_TYPES } from '@/lib/buyersV2';
import { DatePickerInput } from '@/components/DatePickerInput';

const EMPTY = { property_address: '', city: '', zip: '', folio: '', property_type: 'SFR', beds: '', baths: '', sqft: '', purchase_price: '', purchase_date: '', outcome: 'Not verified' };
const cls = 'bg-white border-gray-300 h-8 text-xs';

function AddRow({ buyerId, onAdded, onCancel }) {
  const [f, setF] = useState(EMPTY);
  const up = (k) => (e) => setF(p => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const save = async () => {
    if (!f.property_address) { toast.error('Address required'); return; }
    try { await API.post(`/buyers/${buyerId}/purchases`, f); toast.success('Purchase added'); setF(EMPTY); onAdded(); }
    catch { toast.error('Failed to add'); }
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 p-3 rounded-lg border border-[#0073ea]/30 bg-blue-50/40" data-testid="add-purchase-form">
      <Input data-testid="purchase-address" placeholder="Address" value={f.property_address} onChange={up('property_address')} className={`${cls} md:col-span-2`} />
      <Input data-testid="purchase-city" placeholder="City" value={f.city} onChange={up('city')} className={cls} />
      <Input data-testid="purchase-zip" placeholder="Zip" value={f.zip} onChange={up('zip')} className={cls} />
      <Input placeholder="Folio" value={f.folio} onChange={up('folio')} className={cls} />
      <Select value={f.property_type} onValueChange={up('property_type')}><SelectTrigger className={cls}><SelectValue /></SelectTrigger><SelectContent className="bg-white">{PROPERTY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
      <Input placeholder="Beds" type="number" value={f.beds} onChange={up('beds')} className={cls} />
      <Input placeholder="Baths" type="number" value={f.baths} onChange={up('baths')} className={cls} />
      <Input placeholder="SqFt" type="number" value={f.sqft} onChange={up('sqft')} className={cls} />
      <Input data-testid="purchase-price" placeholder="Price" type="number" value={f.purchase_price} onChange={up('purchase_price')} className={cls} />
      <DatePickerInput testid="purchase-date" value={f.purchase_date} onChange={up('purchase_date')} placeholder="Purchase date" className={`${cls} border-gray-300 bg-white`} />
      <Select value={f.outcome} onValueChange={up('outcome')}><SelectTrigger className={cls}><SelectValue /></SelectTrigger><SelectContent className="bg-white">{['Not verified', 'Kept', 'Flipped'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
      <div className="col-span-2 md:col-span-6 flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}><X className="h-4 w-4" /></Button>
        <Button size="sm" data-testid="save-purchase-btn" onClick={save} className="bg-[#0073ea] hover:bg-[#0060c2] text-white">Save Purchase</Button>
      </div>
    </div>
  );
}

const COLS = ['Date', 'Address', 'City / Zip', 'Folio', 'Type', 'Bd/Ba', 'SqFt', 'Price', 'Cash', 'Outcome', 'Resale', 'Hold', 'Listed', ''];

export function PurchasesCard({ buyer, onChanged }) {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const load = () => API.get(`/buyers/${buyer.id}/purchases`).then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [buyer.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => { load(); onChanged(); };
  const del = async (p) => { if (!window.confirm('Delete this purchase?')) return; await API.delete(`/buyers/${buyer.id}/purchases/${p.id}`); refresh(); };

  return (
    <Card className="bg-white border-gray-200 shadow-sm" data-testid="purchases-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-base font-semibold text-gray-900 flex items-center gap-2"><History className="h-4 w-4 text-[#0073ea]" /> Purchase History <span className="text-gray-400 font-normal text-sm">{items.length}</span></p>
          <Button size="sm" variant="outline" data-testid="add-purchase-btn" onClick={() => setAdding(true)}><Plus className="h-3.5 w-3.5 mr-1" /> Add Purchase</Button>
        </div>
        {buyer.locations?.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="location-breakdown">
            {buyer.locations.map(l => <Badge key={l.label} variant="outline" className="bg-white text-gray-700 border-gray-200"><MapPin className="h-3 w-3 mr-1 text-[#0073ea]" />{l.label} <b className="ml-1">×{l.count}</b></Badge>)}
          </div>
        )}
        {adding && <AddRow buyerId={buyer.id} onAdded={refresh} onCancel={() => setAdding(false)} />}
        {items.length === 0 ? <p className="text-sm text-gray-400 text-center py-3">No purchases recorded</p> : (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500 uppercase text-[10px]"><tr>{COLS.map(c => <th key={c} className="px-2 py-2 text-left font-medium whitespace-nowrap">{c}</th>)}</tr></thead>
              <tbody>
                {items.map(p => (
                  <tr key={p.id} data-testid={`purchase-${p.id}`} className="border-t border-gray-100">
                    <td className="px-2 py-2 whitespace-nowrap">{shortDate(p.purchase_date)}</td>
                    <td className="px-2 py-2 whitespace-nowrap font-medium text-gray-900">{p.property_address}</td>
                    <td className="px-2 py-2 whitespace-nowrap">{[p.city, p.zip].filter(Boolean).join(' ')}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-gray-500">{p.folio}</td>
                    <td className="px-2 py-2">{p.property_type}</td>
                    <td className="px-2 py-2">{p.beds || '—'}/{p.baths || '—'}</td>
                    <td className="px-2 py-2">{p.sqft || '—'}</td>
                    <td className="px-2 py-2 font-mono">{money(p.purchase_price ?? p.price)}</td>
                    <td className="px-2 py-2">{p.cash}</td>
                    <td className="px-2 py-2">{p.outcome}</td>
                    <td className="px-2 py-2 whitespace-nowrap">{p.resale_date ? `${shortDate(p.resale_date)} ${money(p.resale_price)}` : '—'}</td>
                    <td className="px-2 py-2">{p.hold_days ? `${p.hold_days}d` : '—'}</td>
                    <td className="px-2 py-2 whitespace-nowrap">{p.currently_listed ? `Yes ${money(p.current_list_price)}` : 'No'}</td>
                    <td className="px-2 py-2"><button onClick={() => del(p)} className="p-2 -m-1 sm:p-0 sm:m-0 text-gray-300 hover:text-red-500" data-testid={`delete-purchase-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
